 import { useEffect, useState } from "react";
import { Row, Col, Spin, Button, Tag, Typography, Flex } from "antd";
import { useNavigate } from "react-router-dom";
import { EyeOutlined, PhoneOutlined } from "@ant-design/icons";
import axiosInstance from "../api/axiosInstance";
import { useCity } from "../context/CityContext";

const { Title, Paragraph } = Typography;

const TravelersFavoriteChoices = () => {
  const [toursPackage, setToursPackage] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const navigate = useNavigate();

  // ==========================================
  // SELECTED CITY
  // ==========================================
  const { selectedCity } = useCity();

  const citySlug = selectedCity?.slug || "";
  const cityName = selectedCity?.name || "";

  // ==========================================
  // FETCH ALL TOURS FOR SELECTED CITY
  // ==========================================
  useEffect(() => {
    if (citySlug) {
      setToursPackage([]);
      setPage(1);
      setPageCount(1);

      fetchTours(1);
    } else {
      setToursPackage([]);
    }
  }, [citySlug]);

  const fetchTours = async (pageNumber = 1) => {
    if (!citySlug) return;

    try {
      setLoading(true);

      const pageSize = 4;

      const res = await axiosInstance.get("/product-details", {
        params: {
          // =====================================
          // ⭐ ONLY CITY FILTER
          // =====================================
          "filters[city][slug][$eq]": citySlug,

          // =====================================
          // PAGINATION
          // =====================================
          "pagination[page]": pageNumber,
          "pagination[pageSize]": pageSize,

          // =====================================
          // FIELDS
          // =====================================
          "fields[0]": "title",
          "fields[1]": "slug",
          "fields[2]": "final_price",
          "fields[3]": "old_price",
          "fields[4]": "save_price",
          "fields[5]": "duration",
          

          // =====================================
          // IMAGES
          // =====================================
          "populate[images][fields][0]": "url",

          // =====================================
          // SUB PRODUCT
          // =====================================
          "populate[sub_product][fields][0]": "slug",

          // =====================================
          // CITY
          // =====================================
          "populate[city][fields][0]": "name",
          "populate[city][fields][1]": "slug",
        },
      });

      const newData = res.data.data || [];
      const pagination = res.data.meta?.pagination;
 

      setPageCount(pagination?.pageCount || 1);

      setToursPackage((prev) =>
        pageNumber === 1
          ? newData
          : [...prev, ...newData]
      );

      setPage(pageNumber);

    } catch (error) {
      console.error("Tours API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // SEE MORE
  // ==========================================
  const handleSeeMore = () => {
    if (loading) return;

    if (page < pageCount) {
      const nextPage = page + 1;

      fetchTours(nextPage);
    }
  };

  // ==========================================
  // INITIAL LOADING
  // ==========================================
  if (loading && page === 1) {
    return (
      <div className="flex justify-center py-10">
        <Spin
          size="large"
          tip={`Loading ${cityName} Tours...`}
        />
      </div>
    );
  }

  return (
    <div className="">

      {/* ==========================================
          HEADER
      ========================================== */}
      <div className="flex justify-between items-center mb-6 font-medium">

        <div>
          <Title
            className="!font-medium !text-danger !text-2xl !mb-1"
            level={2}
          >
            <span className="text-primary">
              Travelers' 
            </span>{" "}
             Favorite Choices in {cityName}
          </Title>

          <Paragraph className="!mb-0">
            Explore the best tours, attractions and experiences
            in {cityName}
          </Paragraph>
        </div>

      </div>


      {/* ==========================================
          TOURS
      ========================================== */}
      <Row gutter={[16, 16]}>

        {toursPackage.length > 0 ? (

          toursPackage.map((tour) => {

            const title =
              tour.title ||
              tour.attributes?.title ||
              "Untitled Tour";

            const subSlug =
              tour.sub_product?.slug ||
              tour.attributes?.sub_product?.data
                ?.attributes?.slug ||
              "tour";

            const detailSlug =
              tour.slug ||
              tour.attributes?.slug ||
              title
                ?.toLowerCase()
                .replace(/\s+/g, "-") ||
              "details";

            const type =
              tour.type || "Standard";

            const final_price =
              tour.final_price ?? 0;

            const old_price =
              tour.old_price;

            const save_price =
              tour.save_price;

            const duration =
              tour.duration || "N/A";

            const imageUrl =
              tour.images?.[0]?.url ||
              tour.attributes?.images?.data?.[0]
                ?.attributes?.url ||
              "https://via.placeholder.com/400x250?text=No+Image";


            return (

              <Col
                xs={24}
                sm={12}
                md={6}
                key={tour.id}
              >

                <div
                  className="
                    bg-white
                    rounded-xl
                    overflow-hidden
                    relative
                    shadow-custom
                    hover:shadow-2xl
                    h-full
                  "
                >

                  {/* IMAGE */}
                  <div
                    className="relative cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/${subSlug}/${detailSlug}`
                      )
                    }
                  >

                    {/* PRICE OVERLAY */}
                    <div className="absolute overflow-hidden bg-black/20 w-full h-full">

                      <div className="absolute bottom-0 left-0 right-0 p-4 text-left">

                        <div className="text-base font-semibold text-white">
                          AED {final_price}
                        </div>

                        {old_price && (
                          <div className="line-through font-normal text-white text-md">
                            AED {old_price}
                          </div>
                        )}

                        {save_price && (
                          <div className="font-normal text-white text-md">
                            AED {save_price}
                          </div>
                        )}

                      </div>

                    </div>


                    {/* IMAGE */}
                    <img
                      src={`${process.env.REACT_APP_API_URL}${imageUrl}`}
                      alt={title}
                      className="w-full h-[170px] object-cover"
                    />


                    {/* RATING */}
                    <div
                      className="
                        bg-primary
                        absolute
                        bottom-[-25px]
                        right-6
                        rounded-full
                        shadow-lg
                        text-white
                        text-sm
                        flex
                        justify-center
                        w-[80px]
                        h-[80px]
                      "
                    >

                      <div className="text-center leading-tight mt-2">

                        <div className="text-3xl">
                          ★
                        </div>

                        <div className="text-xs font-normal">
                          4.9 (523)
                        </div>

                      </div>

                    </div>

                  </div>


                  {/* CONTENT */}
                  <div className="p-5 pt-3">

                    {/* TOUR TYPE */}
                    <div className="mt-3 mb-2 text-muted text-[11px] font-medium">

                      Tour Type

                      <Tag
                        className="
                          bg-blue-100
                          text-black
                          font-medium
                          rounded
                          border-0
                          text-[11px]
                          ml-2
                        "
                      >
                        {type}
                      </Tag>

                    </div>


                    {/* TITLE */}
                    <div className="font-medium text-black text-md leading-snug">
                      {title}
                    </div>


                    {/* DURATION */}
                    <div className="text-primary mt-2 font-medium">
                      {duration}
                    </div>


                    {/* BUTTONS */}
                    <Flex
                      className="flex mt-4 gap-3"
                       
                    >

                      <Button
                        type="danger"
                        size="small"
                        icon={
                          <EyeOutlined className="text-base" />
                        }
                        className="
                          font-medium
                          flex-1
                          py-3
                          text-xs
                          bg-danger
                          hover:bg-red-700
                          border-none
                          text-white
                          w-full
                          h-auto
                        "
                        onClick={() =>
                          navigate(
                            `/${subSlug}/${detailSlug}`
                          )
                        }
                      >
                        View Details
                      </Button>


                      <Button
                        size="middle"
                        type="default"
                        icon={
                          <PhoneOutlined
                            className="text-base rotate-90"
                          />
                        }
                        className="
                          font-medium
                          flex-1
                          py-3
                          text-xs
                          border-gray-400
                          text-gray-500
                          rounded-lg
                          h-auto
                        "
                      >
                        Call US
                      </Button>

                    </Flex>

                  </div>

                </div>

              </Col>

            );
          })

        ) : (

          <Col span={24}>
            <p className="text-center text-gray-500">
              No tours available in {cityName}.
            </p>
          </Col>

        )}

      </Row>


      {/* ==========================================
          SEE MORE
      ========================================== */}
      {page < pageCount && (

        <div className="text-center mt-6">

          <Button
            onClick={handleSeeMore}
            loading={loading}
            className="
              font-semibold
              w-60
              py-6
              text-sm
              rounded-lg
              text-danger
              border-gray-400
            "
          >
            {loading
              ? "Loading..."
              : "See More"}
          </Button>

        </div>

      )}

    </div>
  );
};

export default TravelersFavoriteChoices;